// packages/ui/src/components/HelpMenu.tsx
import { useEffect, useId, useRef, useState, type ReactElement } from 'react';
import { createPortal } from 'react-dom';
import { CircleHelp } from 'lucide-react';
import type { PlatformBridge } from '@slate/core';
import { helpPagesByGroup, helpUrl } from '../help/helpPages';

/**
 * Header "Help ▾" button + grouped mega-menu popover. Each item opens the
 * corresponding page on the help site in the user's browser via the platform
 * bridge. Built on the same portal pattern as the toolbar's save menu.
 */
export function HelpMenu({ platform }: { platform: PlatformBridge }): ReactElement {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; right: number }>({ top: 0, right: 0 });
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuId = useId();
  const groups = helpPagesByGroup();

  useEffect(() => {
    if (!open) return;
    const b = btnRef.current?.getBoundingClientRect();
    if (b) setPos({ top: b.bottom + 4, right: Math.max(8, window.innerWidth - b.right) });
    const onDocClick = (e: MouseEvent) => {
      if (
        !menuRef.current?.contains(e.target as Node) &&
        !btnRef.current?.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const pick = (slug: string): void => {
    setOpen(false);
    btnRef.current?.focus();
    void platform.openExternal(helpUrl(slug)).catch(() => {});
  };

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        className="mp-icon-btn mp-help-menu__trigger"
        aria-label="Help"
        title="Help"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={() => setOpen((o) => !o)}
      >
        <CircleHelp size={15} aria-hidden />
      </button>
      {open &&
        createPortal(
          <div
            ref={menuRef}
            id={menuId}
            role="menu"
            aria-label="Help"
            className="mp-help-menu"
            style={{ position: 'fixed', top: pos.top, right: pos.right }}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                setOpen(false);
                btnRef.current?.focus();
              }
            }}
          >
            <div className="mp-help-menu__cols">
              {groups.map((g) => (
                <div key={g.group} className="mp-help-menu__col">
                  <div className="mp-help-menu__group" aria-hidden>
                    {g.group}
                  </div>
                  {g.pages.map((p) => (
                    <button
                      key={p.slug || 'home'}
                      type="button"
                      role="menuitem"
                      className="mp-help-menu__item"
                      onClick={() => pick(p.slug)}
                    >
                      {p.title}
                    </button>
                  ))}
                </div>
              ))}
            </div>
            <button
              type="button"
              role="menuitem"
              className="mp-help-menu__home"
              onClick={() => pick('')}
            >
              Open Help Home →
            </button>
          </div>,
          document.body,
        )}
    </>
  );
}
